/**
 * TableExplorer - File explorer for selecting tables and linked tables
 *
 * Displays the complete folder structure from the workspace with
 * tables (and optionally linked tables) nested inside their respective folders.
 * Adapted from the Excel plugin's DataSourceTree component.
 *
 * @module TableExplorer
 */

import { useState, useMemo } from 'react';
import { ChevronRight, Table, Link2, Folder, FolderOpen, Loader2, Home } from 'lucide-react';
import type { FolderTreeNode } from '@loader/shared-types';

import { cn } from '@/lib/utils';
import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks/useLinkedTableQueries';
import { useFolderTreeQuery } from '@/features/organization/hooks/useFolderQueries';
import type { Table as TableType, LinkedTable } from '@/lib/types';

// ============================================================================
// TYPES
// ============================================================================

export type DataSourceType = 'table' | 'linked_table';

export interface DataSourceSelection {
  id: string;
  name: string;
  type: DataSourceType;
}

interface TableExplorerProps {
  /** Currently selected ID */
  value: string;
  /** Callback when selection changes */
  onValueChange: (id: string, name: string, type?: DataSourceType) => void;
  /** Whether the explorer is disabled */
  disabled?: boolean;
  /** Whether to include linked tables in the explorer */
  includeLinkedTables?: boolean;
  /** IDs to exclude from the list */
  excludeIds?: string[];
}

interface FolderNodeProps {
  folder: FolderTreeNode;
  tables: TableType[];
  linkedTables: LinkedTable[];
  selectedValue: string;
  onSelect: (id: string, name: string, type: DataSourceType) => void;
  disabled?: boolean;
  level?: number;
  includeLinkedTables: boolean;
}

interface DataSourceItemProps {
  name: string;
  type: DataSourceType;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

// ============================================================================
// DATA SOURCE ITEM COMPONENT (Table or Linked Table)
// ============================================================================

function DataSourceItem({ name, type, isSelected, onSelect, disabled }: DataSourceItemProps) {
  const icon =
    type === 'table' ? (
      <Table className="size-4 shrink-0 text-blue-500" />
    ) : (
      <Link2 className="size-4 shrink-0 text-purple-500" />
    );

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 rounded-md pl-4 pr-2 py-1.5 text-sm transition-colors',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        isSelected && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      )}
    >
      {icon}
      <span className="truncate text-left">{name}</span>
    </button>
  );
}

// ============================================================================
// FOLDER NODE COMPONENT
// ============================================================================

function FolderNode({
  folder,
  tables,
  linkedTables,
  selectedValue,
  onSelect,
  disabled,
  level = 0,
  includeLinkedTables,
}: FolderNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Filter items that belong to this folder
  const folderTables = useMemo(
    () => tables.filter((t) => t.folder_id === folder.id),
    [tables, folder.id]
  );
  const folderLinkedTables = useMemo(
    () => (includeLinkedTables ? linkedTables.filter((lt) => lt.folder_id === folder.id) : []),
    [linkedTables, folder.id, includeLinkedTables]
  );

  // Check if folder has any content (recursively check children too)
  const hasDirectContent = folderTables.length > 0 || folderLinkedTables.length > 0;

  // Filter children that have content
  const childrenWithContent = useMemo(() => {
    const hasContentRecursive = (f: FolderTreeNode): boolean => {
      const hasTables = tables.some((t) => t.folder_id === f.id);
      const hasLinkedTables = includeLinkedTables && linkedTables.some((lt) => lt.folder_id === f.id);
      if (hasTables || hasLinkedTables) return true;
      return f.children.some(hasContentRecursive);
    };
    return folder.children.filter(hasContentRecursive);
  }, [folder.children, tables, linkedTables, includeLinkedTables]);

  // Hide folder if it has no content at all
  if (!hasDirectContent && childrenWithContent.length === 0) {
    return null;
  }

  return (
    <div className="select-none">
      {/* Folder header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md pl-4 pr-2 py-1.5 text-sm font-medium',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
        )}
      >
        <ChevronRight
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-200',
            isExpanded && 'rotate-90'
          )}
        />
        {isExpanded ? (
          <FolderOpen className="size-4 shrink-0 fill-amber-500 text-amber-600" />
        ) : (
          <Folder className="size-4 shrink-0 fill-amber-500 text-amber-600" />
        )}
        <span className="flex-1 text-left truncate">{folder.name}</span>
      </button>

      {/* Folder children */}
      {isExpanded && (
        <div
          className="border-l border-border"
          style={{ marginLeft: `${level * 16 + 16}px` }}
        >
          {/* Subfolders first */}
          {childrenWithContent.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              tables={tables}
              linkedTables={linkedTables}
              selectedValue={selectedValue}
              onSelect={onSelect}
              disabled={disabled}
              level={level + 1}
              includeLinkedTables={includeLinkedTables}
            />
          ))}

          {/* Tables in this folder */}
          {folderTables.map((table) => (
            <DataSourceItem
              key={table.id}
              name={table.name}
              type="table"
              isSelected={selectedValue === table.id}
              onSelect={() => onSelect(table.id, table.name, 'table')}
              disabled={disabled}
            />
          ))}

          {/* Linked tables in this folder */}
          {folderLinkedTables.map((lt) => (
            <DataSourceItem
              key={lt.id}
              name={lt.name}
              type="linked_table"
              isSelected={selectedValue === lt.id}
              onSelect={() => onSelect(lt.id, lt.name, 'linked_table')}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LOADING SKELETON
// ============================================================================

function TreeSkeleton() {
  return (
    <div
      className="flex items-center justify-center py-8 text-muted-foreground"
      role="status"
      aria-busy="true"
      aria-label="Loading tables"
    >
      <Loader2 className="size-5 animate-spin mr-2" aria-hidden="true" />
      <span className="text-sm">Loading...</span>
    </div>
  );
}

// ============================================================================
// EMPTY STATE
// ============================================================================

function TreeEmpty({ includeLinkedTables }: { includeLinkedTables: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
      <Table className="size-8 mb-2 opacity-50" />
      <p className="text-sm">No {includeLinkedTables ? 'tables or linked tables' : 'tables'} found</p>
      <p className="text-xs">Create {includeLinkedTables ? 'one' : 'a table'} first</p>
    </div>
  );
}

// ============================================================================
// HOME HEADER COMPONENT (matches main app's sidebar)
// ============================================================================

function HomeHeader() {
  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
      )}
    >
      <Home className="size-4 shrink-0" />
      <span className="flex-1 text-left truncate">Home</span>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TableExplorer({
  value,
  onValueChange,
  disabled,
  includeLinkedTables = false,
  excludeIds = [],
}: TableExplorerProps) {
  const { data: tablesData = [], isLoading: isLoadingTables } = useTablesListQuery({
    status: 'active',
    limit: 100,
  });
  const { data: linkedTablesData = [], isLoading: isLoadingLinkedTables } = useLinkedTablesListQuery({
    status: 'active',
    enabled: includeLinkedTables,
  });
  const { data: folderTree = [], isLoading: isLoadingFolders } = useFolderTreeQuery();

  const isLoading = isLoadingTables || isLoadingFolders || (includeLinkedTables && isLoadingLinkedTables);

  // Filter out excluded IDs
  const tables = useMemo(
    () => tablesData.filter((t) => !excludeIds.includes(t.id)),
    [tablesData, excludeIds]
  );
  const linkedTables = useMemo(
    () => (includeLinkedTables ? linkedTablesData.filter((lt) => !excludeIds.includes(lt.id)) : []),
    [linkedTablesData, excludeIds, includeLinkedTables]
  );

  // Root level items (not in any folder)
  const rootTables = useMemo(
    () => tables.filter((t) => t.folder_id === null || t.folder_id === undefined),
    [tables]
  );
  const rootLinkedTables = useMemo(
    () => linkedTables.filter((lt) => lt.folder_id === null || lt.folder_id === undefined),
    [linkedTables]
  );

  const isEmpty = tables.length === 0 && linkedTables.length === 0;

  // Wrapper to ensure type is always passed
  const handleSelect = (id: string, name: string, type: DataSourceType = 'table') => {
    onValueChange(id, name, type);
  };

  if (isLoading) {
    return <TreeSkeleton />;
  }

  if (isEmpty) {
    return <TreeEmpty includeLinkedTables={includeLinkedTables} />;
  }

  return (
    <div className="space-y-0.5 max-h-[300px] overflow-y-auto border rounded-md p-2">
      {/* Home header - like main app sidebar */}
      <HomeHeader />

      {/* Content under Home - with indent line */}
      <div className="border-l border-border" style={{ marginLeft: '16px' }}>
        {/* Folders first */}
        {folderTree.map((folder) => (
          <FolderNode
            key={folder.id}
            folder={folder}
            tables={tables}
            linkedTables={linkedTables}
            selectedValue={value}
            onSelect={handleSelect}
            disabled={disabled}
            level={1}
            includeLinkedTables={includeLinkedTables}
          />
        ))}

        {/* Root level tables (not in any folder) */}
        {rootTables.map((table) => (
          <DataSourceItem
            key={table.id}
            name={table.name}
            type="table"
            isSelected={value === table.id}
            onSelect={() => handleSelect(table.id, table.name, 'table')}
            disabled={disabled}
          />
        ))}

        {/* Root level linked tables (not in any folder) */}
        {rootLinkedTables.map((lt) => (
          <DataSourceItem
            key={lt.id}
            name={lt.name}
            type="linked_table"
            isSelected={value === lt.id}
            onSelect={() => handleSelect(lt.id, lt.name, 'linked_table')}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}
